import React, { useState } from 'react';
import axios from 'axios';
import {
  X,
  Plus,
  Minus,
  Loader2,
  AlertCircle,
  CheckCircle,
  Calendar,
  AlertTriangle,
} from 'lucide-react';
import { getApiBase, getAuthHeaders } from '../../utils/api';

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const RemoveSeatsModal = ({ organizationId, seatType, currentSeats, periodEnd, onClose, onSuccess }) => {
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const seatLabel = seatType === 'executive' ? 'Executive' : 'EA';

  // Calculate removable seats (purchased minus already scheduled for removal)
  const removableSeats = currentSeats.purchased - (currentSeats.scheduled_removal || 0);
  const maxRemovable = Math.min(removableSeats, quantity);

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      await axios.post(
        `${getApiBase()}/organizations/${organizationId}/seats/remove`,
        {
          seat_type: seatType,
          quantity,
        },
        { headers: getAuthHeaders() }
      );
      setSuccess(true);
      setTimeout(() => {
        onSuccess();
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to schedule seat removal');
    } finally {
      setLoading(false);
    }
  };

  const incrementQuantity = () => setQuantity((q) => Math.min(q + 1, removableSeats));
  const decrementQuantity = () => setQuantity((q) => Math.max(q - 1, 1));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Remove {seatLabel} Seats
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          {success ? (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Removal Scheduled!
              </h3>
              <p className="text-gray-600">
                {quantity} {seatLabel} seat{quantity !== 1 ? 's' : ''} will be removed on{' '}
                {formatDate(periodEnd)}.
              </p>
            </div>
          ) : removableSeats === 0 ? (
            <div className="text-center py-8">
              <AlertTriangle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No Removable Seats
              </h3>
              <p className="text-gray-600">
                You can only remove purchased seats. The included seats in your plan cannot be removed.
              </p>
              {currentSeats.scheduled_removal > 0 && (
                <p className="text-sm text-orange-600 mt-2">
                  {currentSeats.scheduled_removal} seat{currentSeats.scheduled_removal !== 1 ? 's' : ''} already scheduled for removal.
                </p>
              )}
            </div>
          ) : (
            <>
              {/* Current Status */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="text-sm text-gray-600 mb-1">Removable {seatLabel} seats</div>
                <div className="text-lg font-semibold text-gray-900">
                  {removableSeats} of {currentSeats.purchased} purchased seat{currentSeats.purchased !== 1 ? 's' : ''}
                </div>
                {currentSeats.scheduled_removal > 0 && (
                  <div className="text-sm text-orange-600 mt-1">
                    ({currentSeats.scheduled_removal} already scheduled for removal)
                  </div>
                )}
              </div>

              {/* Quantity Selector */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  How many seats to remove?
                </label>
                <div className="flex items-center justify-center gap-4">
                  <button
                    onClick={decrementQuantity}
                    disabled={quantity <= 1}
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Minus className="w-5 h-5 text-gray-600" />
                  </button>
                  <input
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, Math.min(removableSeats, parseInt(e.target.value) || 1)))}
                    className="w-20 text-center text-2xl font-bold border border-gray-300 rounded-lg py-2"
                    min="1"
                    max={removableSeats}
                  />
                  <button
                    onClick={incrementQuantity}
                    disabled={quantity >= removableSeats}
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Plus className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
              </div>

              {/* Important Notice */}
              <div className="border border-orange-200 bg-orange-50 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-orange-900 mb-1">
                      Removal takes effect: {formatDate(periodEnd)}
                    </h4>
                    <p className="text-sm text-orange-700">
                      Seats will remain available until the end of your current billing period.
                      No refund will be issued.
                    </p>
                  </div>
                </div>
              </div>

              {/* Warning */}
              <div className="flex items-start gap-2 text-sm text-gray-600 bg-gray-50 rounded-lg p-3 mb-4">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-yellow-500" />
                <span>
                  Make sure you don't have more users than available seats before the removal date,
                  or they may lose access.
                </span>
              </div>

              {/* Error */}
              {error && (
                <div className="flex items-center gap-2 text-red-600 bg-red-50 rounded-lg p-3 mb-4">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!success && removableSeats > 0 && (
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Schedule Removal
            </button>
          </div>
        )}

        {(success || removableSeats === 0) && (
          <div className="flex items-center justify-end px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RemoveSeatsModal;
